import {Injectable} from '@angular/core';
import {Storage} from '@ionic/storage';
import {BehaviorSubject, from} from 'rxjs';
import {Estacao} from 'src/app/shared/models/estacao.model';
import {loading} from '../util/loading';
import {toast} from '../util/toast';
import {EstacaoService} from './estacao.service';
import {AbstractService} from './abstract.service';
import {HttpClient} from '@angular/common/http';
import {MONITORAR_TOKEN_FIREBASE} from './notificacao.service';

const EstacaoKey = 'fav-monitorar-estacao';
const EstacaoKeyWidgetFavoritoClicked = 'fav-monitorar-estacao-widget-favorito-clicked';
const EstacaoKeyWidgetEmtpyStateClicked = 'fav-monitorar-estacao-widget-empty-state-clicked';

@Injectable({
  providedIn: 'root'
})
export class FavoritoService extends AbstractService<any> {

  change$: BehaviorSubject<Estacao[]> = new BehaviorSubject(null);

  constructor(
      http: HttpClient,
      private storage: Storage,
      private estacaoService: EstacaoService,
  ) {
    super('v1/notificacoesCidadao', http);
  }

  async init() {
    return await this.storage.get(EstacaoKey) || [];
  }

  async favoritarEstacao(estacao: Estacao) {
    const estacoesFavorito = await this.init();
    const existeEstacao = estacoesFavorito.find(e => e.idEstacao === estacao.idEstacao);
    if (!existeEstacao) {
      const load = this.estacaoService.buscarPorIds(estacao.idEstacao.toString()).subscribe(estacoes => {
        toast(`Estação ${estacao.isPrincipal ? 'principal' : ''} ${estacao.noEstacao} ${estacao.isPrincipal ?
          'adicionada' : 'favoritada'} com sucesso!`);
        const est = estacoes[0];
        est.isPrincipal = estacao.isPrincipal;
        estacoesFavorito.push(est);

        const token = localStorage.getItem(MONITORAR_TOKEN_FIREBASE);

        from(this.init()).subscribe(estacoesFavoritas => {
          const principal = estacoesFavoritas.find(x => x.isPrincipal);
          const favoritas = estacoesFavoritas.filter(x => !x.isPrincipal).map(x => x.idEstacao);

          favoritas.push(estacao.idEstacao);

          const dispositivo = {
            ativo: true,
            idEstacao: principal ? principal.idEstacao : null,
            idsEstacoesFavoritas: favoritas,
            token
          };

          this.post(dispositivo).subscribe(() => {
            console.log('Dispositivo atualizado com sucesso.');
          });
        });
        return this.sync(estacoesFavorito);
      });
      await loading(load);
    } else {
      await toast(`Estação ${estacao.noEstacao} já existe na lista de estações selecionadas!`);
    }
  }

  async desfavoritarEstacao(estacao: Estacao): Promise<Estacao[]> {
    const estacoes = await this.init();
    if (!estacoes.length) {
      return;
    }
    const est = estacoes.find(x => x.idEstacao === estacao.idEstacao);
    const favoritos = estacoes.filter(item => item.idEstacao !== estacao.idEstacao);
    await toast(`Estação ${est.isPrincipal ? 'principal' : ''} ${est.noEstacao} ${est.isPrincipal ?
      'removida' : 'desfavoritada'} com sucesso!`);
    return this.sync(favoritos);
  }

  async isEstacaoFavorito(estacao: Estacao) {
    const estacoesFavorito = await this.init();
    if (estacoesFavorito && estacoesFavorito.length) {
      return estacoesFavorito.some((item) => estacao.idEstacao === item.idEstacao);
    }
    return false;
  }

  async removerEstacaoPrincipal() {

    const estacoes = await this.init();
    let estacoesFavoritas = [];

    if (estacoes.length) {
      estacoesFavoritas = estacoes.filter(estacaoFilter => !estacaoFilter.isPrincipal);
    }

    return this.sync(estacoesFavoritas);
  }

  async trocarEstacaoPrincipal(estacao: Estacao) {

    const estacoes = await this.init();
    let estacoesFavoritas = [];

    if (estacoes.length) {
      estacoesFavoritas = estacoes.filter(estacaoFilter => !estacaoFilter.isPrincipal);
    }

    const existeEstacao = estacoesFavoritas.find(e => e.idEstacao === estacao.idEstacao);
    if (existeEstacao) {
      existeEstacao.isPrincipal = true;
    } else {
      estacao.isPrincipal = true;
      estacoesFavoritas.push(estacao);
    }

    return this.sync(estacoesFavoritas);
  }

  private sync(estacoes: Estacao[]): Promise<Estacao[]> {
    return this.storage.set(EstacaoKey, estacoes).then(data => {
      this.change$.next(estacoes);
      return data;
    }).catch(() => alert('Não foi possível buscar estações favoritas'));
  }

  async getEstacoesFavoritas2() {
    console.log(await this.init());
    await this.getEstacoesFavoritas2();
    return this.init();
  }

  async getEstacoesFavoritas() {
    const token = localStorage.getItem(MONITORAR_TOKEN_FIREBASE);
    const localFavorites = await this.init();
    const serverFavorites = await this.getAll(`${token}`).toPromise();
    const localIds = localFavorites.map(a => a.idEstacao);
    const justLocalFavorites = serverFavorites.filter((estacao) => !localIds.includes(estacao.idEstacao));

    return serverFavorites.concat(justLocalFavorites);
  }

  atualizarEstacoes(estacoes: Estacao[]) {
    this.sync(estacoes);
  }

  setClickedFavorito(favorito) {
    return this.storage.set(EstacaoKeyWidgetFavoritoClicked, favorito);
  }

  setClickedEmptyState() {
    return this.storage.set(EstacaoKeyWidgetEmtpyStateClicked, true);
  }

  async getClickedEmptyState() {
    const emptyState = await this.storage.get(EstacaoKeyWidgetEmtpyStateClicked);
    if (emptyState) {
      await this.storage.remove(EstacaoKeyWidgetEmtpyStateClicked);
    }
    return emptyState;
  }

  async getClickedFavorito() {
    const favorito = await this.storage.get(EstacaoKeyWidgetFavoritoClicked);
    if (favorito) {
      await this.storage.remove(EstacaoKeyWidgetFavoritoClicked);
    }
    return favorito;
  }

  async atualizarOrdem(estacoes: Estacao[]) {
    return this.storage.set(EstacaoKey, estacoes).then(data => {
      return data;
    }).catch(() => alert('Não foi possível atualizar estações favoritas'));
  }
}
